import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import dayjs from 'dayjs';
import { Col, Flex, Form, Input, Row, Tooltip, Spin, Alert } from "antd";
import { find } from 'lodash';
import { PageHeader } from "../../components/PageHeader/PageHeader";
import { getCatalog, getCatalogExport, clearData } from "./catalogSlice";
import { getSavedFilters, createSavedFilter } from "../SavedFilters/savedFiltersSlice";
import { buildQueryString, debounce } from "../../utils/helpers";
import { HeaderCell } from "./HeaderCell";
import { IconDownload } from "../../components/IconDownload/IconDownload";
import { IconSearch } from "../../components/IconSearch/IconSearch";
import { MainButton } from "../../components/MainButton/MainButton";
import { Notification } from "../../components/Notification/Notification";
import { SalesSummary } from "../SalesSummary/SalesSummary";
import { CatalogSearchBox } from "../CatalogSearchBox/CatalogSearchBox";
import { SectionHeader } from "../../components/SectionHeader/SectionHeader";
import { EscapedCSVLink as CSVLink } from "../../components/EscapedCSVLink/EscapedCSVLink";
import { Breadcrumbs } from "../../components/Breadcrumbs/Breadcrumbs";
import { valueCell } from "../../components/ValueCell/ValueCell";
import { dateCell } from "../../components/DateCell/DateCell";
import { TableWithRow as Table} from "../../components/TableWithRow/TableWithRow";
import { exportToXLSX } from "../../utils/helpers";

import css from "./Catalog.module.css";

export const Catalog = () => {  
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const defaultSortColumn = "Sold_At_Date:desc"
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [upcs, setUpcs] = useState([]);
  const [minReleaseDate, setMinReleaseDate] = useState(new Date());
  
  const [search, setSearch] = useState({search: [], format: [], range: {start: null, end: null}});
  const [sortDirection, setSortDirection] = useState(defaultSortColumn);
  const exportButtonRef = React.useRef();
  const [exportInProgress, setExportInProgress] = useState(false);
  const [exportMode, setExportMode] = useState('');
  const [columnsSet, setColumnsSet] = useState(false);
      
  const { catalog, getCatalogInProgress } =
    useSelector((state) => state?.catalog) || {};

  const { catalogExport, getCatalogExportInProgress } =
    useSelector((state) => state?.catalog) || {};

  const { data: rows, totalPages } = catalog || {};
  
  const getFullData = () => {
    setExportMode('table');
    dispatch(
      getCatalogExport({
        search,
        sort: sortDirection,
      })
    ).then((catalogExport) => {
      if (catalogExport !== null) {
        exportButtonRef.current.link.click();
        setExportMode('');
      }
    });
  };

  const handleTableChange = (pagination, filters, sorter) => {
    let { current: page, pageSize = 10 } = pagination;
    const { field, order } = sorter;
    const sort = (order === undefined) ?  defaultSortColumn : `${field}:${order.replace('end', '')}`;
    
    if(sort != sortDirection)
      page = 1;
      
    setPage(page);
    setPageSize(pageSize);
    setSortDirection(sort);
    
    dispatch(
      getCatalog({
        page,
        pageSize, 
        search,
        sort
      })
    );
  };
  
  const renderManagers = (_, rowData) => {
    let { Manager, Manager_Email } = rowData;
    if(!Manager)
      return <span>N/A</span>;
    Manager = Manager.split(',');
    Manager_Email = Manager_Email.split(',');  
    let managers = [];
    for(let i = 0; i < Manager.length; i++) {
      managers.push(<a key={Manager_Email[i]}
          href={`mailto:${Manager_Email[i]}`} 
          className={css.clickableElement} 
        >
          {Manager[i]}
        </a>)
    }
    return <span className={css.managersList}>{managers}</span>     
  }
  
  const columns = [
    {
      title: (
        <Tooltip title="The title of the product / release, the associated primary Artist, and UPC">
          <div>Title</div>
        </Tooltip>
      ),
      editable: true,
      dataIndex: "Title",
      width: 250,
      sorter: true,
      align: "left",
      showSorterTooltip: false,
      render: (_, record) => (
        <div>
          <a 
            href="#" 
            className={css.clickableElement} 
            onClick={(e) => { 
              e.preventDefault(); 
            }}
          >
            <strong>{record.Title}</strong>
          </a>
          <br />
          <a 
            href="#" 
            className={css.clickableElement} 
            onClick={(e) => { 
              e.preventDefault(); 
            }}
          >
            By: {record.Artist}
          </a>
          <br />
          <a 
            href="#" 
            className={css.clickableElement} 
            onClick={(e) => { 
              e.preventDefault(); 
            }}
          >
            UPC: {record.UPC}
          </a>
        </div>
      ),
    },
    {
      title: (
        <Tooltip title="Label Name">
          <div>Label</div>
        </Tooltip>
      ),

      editable: true,
      dataIndex: "Label",
      width: 250,
      sorter: true,
      align: "left",
      showSorterTooltip: false,
    },
    /*
    {
      title: (
        <Tooltip title="The relevant label manager for this release">
          <div>Label Manager</div>
        </Tooltip>
      ),
    
      editable: true,
      dataIndex: "Manager",
      width: 250,
      sorter: true,
      align: "left",
      showSorterTooltip: false,
      hiddenByDefault: true,
      render: (record, rowData) => renderManagers(record, rowData)

    },
    */

    {
      title: (
        <Tooltip title="Catalogue Number">
          <div>Catalogue Number</div>
        </Tooltip>
      ),
      editable: true,
      dataIndex: "Catalogue_Number",
      sorter: true,
      width: 200,
      showSorterTooltip: false,
      align: "left",
      render: (record) => (<a 
        href="#" 
        className={css.clickableElement} 
        onClick={(e) => { 
          e.preventDefault(); 
        }}
      >
        {record}
      </a>)
    },
    {
      title: (
        <Tooltip title='Physical format description (12" Record represents all 12" formats - Maxi Single/EP/LP)'>
          <div>Format</div>
        </Tooltip>
      ),
      editable: true,
      dataIndex: "Format",
      sorter: true,
      width: 150,
      align: "left",
      showSorterTooltip: false,
      className: "format-cell",
    },
    {
      title: (
        <Tooltip title="Release date to consumer of product">
          <div>Release Date</div>
        </Tooltip>
      ),
      editable: true,
      dataIndex: "Release_Date",
      sorter: true,
      width: 200,
      align: "left",
      showSorterTooltip: false,
      render: (record) => dateCell(record)
    },    
    {
      title: (
        <Tooltip title="-TBD-">
          <div>Stock </div>
        </Tooltip>
      ),
      editable: true,
      dataIndex: "Whs_Stk_At_Date",
      sorter: true,
      width: 150,
      align: "left",
      showSorterTooltip: false,
      render: (record) => valueCell(record)
    },
    {
      title: (
        <Tooltip title="-TBD-">
          <div>Sales (1 Month) </div>
        </Tooltip>
      ),
      editable: true,
      dataIndex: "Sold_1M",
      sorter: true,
      width: 150,
      align: "left",
      showSorterTooltip: false,
      render: (record) => valueCell(record)
    },
    {
      title: (
        <Tooltip title="-TBD-">
          <div>Sales (3 Months) </div>
        </Tooltip>
      ),
      editable: true,
      dataIndex: "Sold_3M",
      sorter: true,
      width: 150,
      align: "left",
      showSorterTooltip: false,
      render: (record) => valueCell(record)
    },
    {
      title: (
        <Tooltip title="-TBD-">
          <div>Sales (12 Months) </div>
        </Tooltip>
      ),
      editable: true,
      dataIndex: "Sold_12M",
      sorter: true,
      width: 150,
      align: "left",
      showSorterTooltip: false,
      render: (record) => valueCell(record)
    },
    {
      title: (
        <Tooltip title="-TBD-">
          <div>Sales (At Date) </div>
        </Tooltip>
      ),
      editable: true,
      dataIndex: "Sold_At_Date",
      sorter: true,
      defaultSortOrder: 'descend',
      width: 150,
      align: "left",
      showSorterTooltip: false,
      render: (record) => valueCell(record)
    },
    {
      title: (
        <Tooltip title="-TBD-">
          <div>FOC Ship (1 Month) </div>
        </Tooltip>
      ),
      editable: true,
      dataIndex: "FOC_Cons_1M",
      sorter: true,
      width: 150,
      align: "left",
      showSorterTooltip: false,
      render: (record) => valueCell(record)
    },
    {
      title: (
        <Tooltip title="-TBD-">
          <div>FOC Ship (3 Months) </div>
        </Tooltip>
      ),
      editable: true,
      dataIndex: "FOC_Cons_3M",
      sorter: true,
      width: 150,
      align: "left",
      showSorterTooltip: false,
      render: (record) => valueCell(record)
    },
    {
      title: (
        <Tooltip title="-TBD-">
          <div>FOC Ship (12 Months) </div>
        </Tooltip>
      ),
      editable: true,
      dataIndex: "FOC_Cons_12M",
      sorter: true,
      width: 150,
      align: "left",
      showSorterTooltip: false,
      render: (record) => valueCell(record)
    },
    {
      title: (
        <Tooltip title="-TBD-">
          <div>FOC Ship (At Date) </div>
        </Tooltip>
      ),
      editable: true,
      dataIndex: "FOC_Cons_At_Date",
      sorter: true,
      width: 150,
      align: "left",
      showSorterTooltip: false,
      render: (record) => valueCell(record)
    },
    {
      title: (
        <Tooltip title="-TBD-">
          <div>Returns (1 Month) </div>
        </Tooltip>
      ),
      editable: true,
      dataIndex: "Returns_1M",
      sorter: true,
      width: 150,
      align: "left",
      showSorterTooltip: false,
      render: (record) => valueCell(record)
    },
    {
      title: (
        <Tooltip title="-TBD-">
          <div>Returns (3 Months) </div>
        </Tooltip>
      ),
      editable: true,
      dataIndex: "Returns_3M",
      sorter: true,
      width: 150,
      align: "left",
      showSorterTooltip: false,
      render: (record) => valueCell(record)
    },
    {
      title: (
        <Tooltip title="-TBD-">
          <div>Returns (12 Months) </div>
        </Tooltip>
      ),
      editable: true,
      dataIndex: "Returns_12M",
      sorter: true,
      width: 150,
      align: "left",
      showSorterTooltip: false,
      render: (record) => valueCell(record)
    },
    {
      title: (
        <Tooltip title="-TBD-">
          <div>Returns (At Date) </div>
        </Tooltip>
      ),
      editable: true,
      dataIndex: "Returns_At_Date",
      sorter: true,
      width: 150,
      align: "left",
      showSorterTooltip: false,
      render: (record) => valueCell(record)
    },

  ];   


 



  const handleSort = (column, event) => {
    if (event.shiftKey) {
      event.preventDefault();
      event.stopPropagation();
      return; 
    }
  };

  const [isShiftPressed, setIsShiftPressed] = useState(false);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Shift") {
        setIsShiftPressed(true);
      }
    };
    
    const handleKeyUp = (event) => {
      if (event.key === "Shift") {
        setIsShiftPressed(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, []);

  
  const onApplySearch = (search) => {
    setSearch(search);
    setPage(1);
    
    dispatch(
      getCatalog({
        page: 1,
        pageSize,
        search,
        sort: sortDirection,
      }),
    );

  }
  
  const onResetSearch = (search) => {
    setSearch(search);
    setPage(1);
    setUpcs([]);
    
    dispatch(
      clearData({
      })
    );
  
  }
  
  const endDate = search && search.endDate ? search.endDate : dayjs();
  const formattedEndDate = endDate.format('YYYY-MM');

  
  return (<>
    <Breadcrumbs paths={[{path: `/catalog/`, breadcrumbName: `Catalogue On Hand Report`}]} />
    <PageHeader title="Catalogue On Hand Report">
    </PageHeader>
  
    <Flex className={css.root} vertical>
      <div className={"ibox-holder"}>
        <CatalogSearchBox onApply={onApplySearch} onReset={onResetSearch} />
      </div>
      <Alert message="The data in this portal is provided for analysis only. Please use your sales statements for royalties processing." type="warning" />      
      <div className={"ibox-holder"}>
        <SectionHeader title={`Stock and Sales to ${formattedEndDate}`} titleClassName="mainSectionHeader">
          <Flex align="center" justify="flex-end" gap="10px">
            <CSVLink data={catalogExport || []} linkRef={exportButtonRef} filename={`stock_and_sales_to_${formattedEndDate}.csv`} enclosingCharacter={`"`} />
            { (exportMode == 'table' && getCatalogExportInProgress ) && <Spin size="small" />}
            <a onClick={getFullData} disabled={getCatalogExportInProgress}><IconDownload /></a>
          </Flex>
        </SectionHeader>
        
          <Table
            components={{
              header: {
                row: HeaderCell,
              },
            }}
            columns={columns}
            rowKey={(record) => record.key}
            dataSource={rows}
            pagination={{
              position: ["bottomRight"],
              responsive: true,
              current: page,
              total: totalPages,
              pageSize: pageSize,
              hideOnSinglePage: true,
              showSizeChanger: true,
              pageSizeOptions: ['10', '50', '100', '1000']
            }}
            loading={getCatalogInProgress}
            style={{ marginTop: 10 }}
            scroll={{ x: "100%" }}
            onChange={handleTableChange}
            tableLayout="fixed"
            locale={{ emptyText: 'Enter your search parameters to start' }} 
          />
      </div>
    </Flex>
  </>
  );
};
